import { computed, observable, action, makeObservable } from 'mobx';
import * as t from 'io-ts';
import { jsonParse } from 'src_common/common/jsonParse';
import { createGuard } from 'src_common/common/createGuard';
import { Value } from 'src_common/common/mobx-utils/Value';
import { Amount } from 'src_common/common/amount/Amount';
import { DataTimeDuration } from 'src_common/utils/time/time';
import { SocketConnection, SocketConnectionController, SocketEventType } from './SocketConnection';
import { ConfigState } from './ConfigState';
import { Common } from 'src/domains/common/Common';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';

const NetProfitIO = t.interface({
    action: t.literal('NetPosition'),
    totalLoss: t.number,
    totalProfit: t.number,
    session: t.interface({
        accountId: t.number,
        createdAt: t.string,
        gameId: t.number,
        id: t.number,
    }),
});

const SessionInitiatedIO = t.interface({
    action: t.literal('SessionInitiated'),
    session: t.interface({
        accountId: t.number,
        createdAt: t.string,
        gameId: t.number,
        id: t.number,
    }),
});

const SessionAlreadyExistIO = t.interface({
    action: t.literal('SessionAlreadyExist'),
    session: t.interface({
        accountId: t.number,
        createdAt: t.string,
        gameId: t.number,
        id: t.number,
    }),
});

const ConnectionClosedIO = t.interface({
    action: t.literal('ConnectionClosed'),
});

const KnownMessageIO = t.union([NetProfitIO, SessionInitiatedIO, SessionAlreadyExistIO, ConnectionClosedIO]);

const isKnownMessage = createGuard(KnownMessageIO);

type MessageToType = unknown;

const getCurrentTime = (): number => {
    return new Date().getTime();
};

const currentTime = new Value(getCurrentTime(), (setValue) => {
    const timer = setInterval((): void => {
        setValue(getCurrentTime());
    }, 1000);

    return (): void => {
        clearInterval(timer);
    };
});

class GameStatusInnerState {
    private readonly socketController: SocketConnectionController<MessageToType>;

    @observable private connectionEstablished: boolean = false;
    @observable private startTime: string | null = null;
    @observable private totalWins: Amount = new Amount('0');
    @observable private totalLoss: Amount = new Amount('0');

    private readonly config: ConfigState;
    private pingTimer: ReturnType<typeof setInterval> | null = null;

    public constructor(
        private readonly gameId: number,
        private readonly userId: number,
        private readonly showMultipleSessionPopup: () => void,
        private readonly common: Common
    ) {
        makeObservable(this);

        this.config = new ConfigState(this.common);

        this.socketController = SocketConnection.startSocket(
            `Casino game ${this.gameId} statistic for user ${this.userId}`,
            `${this.common.envVariables.casinoSessionUrl}${this.userId}`,
            10000,
            this.onSocketMessageCallback
        );
    }

    public dispose(): void {
        if (this.pingTimer !== null) {
            clearInterval(this.pingTimer);
            this.pingTimer = null;
        }

        if (this.connectionEstablished) {
            this.socketController.send({
                jwt: this.common.session.currentJwt,
                action: 'closeSession',
            });
        }
        this.connectionEstablished = false;
        this.socketController.dispose();
    }

    private onSocketMessageCallback = (event: SocketEventType<MessageToType>): void => {
        if (event.type === 'socket') {
            if (this.startTime === null) {
                this.socketController.send({
                    gameId: this.gameId,
                    jwt: this.common.session.currentJwt,
                    action: 'initSession',
                });
            }
            return;
        }

        const messageJson = jsonParse(event.message);

        if (messageJson.type === 'json') {
            this.processMessage(messageJson.json);
        } else {
            console.info('unhandled message text', messageJson.text);
        }
    };

    private startSendingPings = (): void => {
        this.pingTimer = setInterval(() => {
            this.socketController.send({
                jwt: this.common.session.currentJwt,
                action: 'extendSession',
            });
        }, 2000);
    };

    @action private processMessage = (socketMessage: unknown): void => {
        if (isKnownMessage(socketMessage)) {
            console.info('socketMessage.action', socketMessage.action);

            if (socketMessage.action === 'SessionInitiated') {
                this.connectionEstablished = true;
                this.startTime = socketMessage.session.createdAt;
                this.startSendingPings();
            }

            if (socketMessage.action === 'NetPosition') {
                this.totalWins = this.config.precision.newFromAnything(socketMessage.totalProfit);
                this.totalLoss = this.config.precision.newFromAnything(socketMessage.totalLoss);
            }

            if (socketMessage.action === 'SessionAlreadyExist') {
                // this action is sent to all tabs so we need to keep the established game session working
                if (this.connectionEstablished === false) {
                    this.showMultipleSessionPopup();
                }
            }
        }
    };

    @computed.struct private get startGameTime(): number | null {
        if (this.startTime === null) {
            return null;
        }

        const time = new Date(this.startTime);
        return time.getTime();
    }

    @computed public get gameDurationCounter(): string {
        let hoursForView = '00';
        let minuterForView = '00';
        let secondsForView = '00';

        if (this.startGameTime !== null && this.startGameTime > 0) {
            const diffInMilliseconds = Math.floor(currentTime.getValue() - this.startGameTime);
            const duration = DataTimeDuration.from(diffInMilliseconds);

            const seconds = duration.seconds();
            const minutes = duration.minutes();
            const hours = duration.hours();

            if (hours > 0 || minutes > 0 || seconds > 0) {
                hoursForView = hours.toString().padStart(2, '0');
                minuterForView = minutes.toString().padStart(2, '0');
                secondsForView = seconds.toString().padStart(2, '0');
            }
        }

        return `${hoursForView}:${minuterForView}:${secondsForView}`;
    }

    @computed public get netPositionForView(): string {
        const currency = BasicDataModel.get(this.common).currency;
        return this.totalWins.sub(this.totalLoss).format(currency);
    }
}

export class GameStatusState {
    @observable private gameStatusInnerStateValue: Value<null | GameStatusInnerState>;

    public constructor(
        private readonly gameId: number,
        private userId: number,
        private showMultipleSessionPopup: () => void,
        private readonly common: Common
    ) {
        makeObservable(this);

        this.gameStatusInnerStateValue = new Value<GameStatusInnerState | null>(null, (setValue) => {
            const state = new GameStatusInnerState(
                this.gameId,
                this.userId,
                this.showMultipleSessionPopup,
                this.common
            );

            setValue(state);

            return (): void => {
                setValue(null);
                state.dispose();
            };
        });
    }

    @computed private get gameStatusInnerState(): GameStatusInnerState | null {
        return this.gameStatusInnerStateValue.getValue();
    }

    @computed public get gameDurationCounter(): string {
        return this.gameStatusInnerState?.gameDurationCounter ?? 'n/a';
    }

    @computed public get netPositionForView(): string {
        return this.gameStatusInnerState?.netPositionForView ?? 'n/a';
    }

    public closeSocket(): void {
        this.gameStatusInnerState?.dispose();
    }
}
